<script setup lang="ts">
  import AuthService from '@/api/services/auth.service';
  import { useAuthStore } from '@/composition/use-auth';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { ref } from 'vue';

  const authStore = useAuthStore();
  const ui = useUi();

  const newPin = ref<null | string>(null);

  const formValid = ref(false);

  const saving = ref(false);
  async function setPin() {
    if (saving.value) return;

    if (!formValid.value) return;

    try {
      saving.value = true;
      await AuthService.setPin(newPin.value!);
      newPin.value = null;
      ui.showToast(AlertType.Success, 'PIN guardado');
    } catch (error) {
      ui.serverError(error, 'No se pudo guardar el PIN');
    } finally {
      saving.value = false;
    }
  }
</script>

<template>
  <div class="page-user-settings">
    <div class="page-inner">
      <v-card elevation="0" class="page-card">
        <v-card-title>
          <v-icon left>mdi-cog</v-icon>
          Perfil
        </v-card-title>
        <v-card-text>
          <div class="profile-form">
            <v-form v-model="formValid" @submit.prevent>
              <v-text-field
                v-model="authStore.user.name"
                prepend-inner-icon="mdi-account"
                label="Nombre"
                outlined
                dense
                disabled
              ></v-text-field>
              <v-text-field
                v-model="authStore.user.email"
                prepend-inner-icon="mdi-email"
                label="Correo electrónico"
                outlined
                dense
                disabled
              ></v-text-field>
              <!-- Pin -->
              <template v-if="newPin === null">
                <v-btn @click="newPin = ''" color="primary">
                  <v-icon left>mdi-lock</v-icon>
                  Cambiar PIN
                </v-btn>
              </template>
              <template v-else>
                <v-text-field
                  v-model="newPin"
                  prepend-inner-icon="mdi-lock"
                  label="Nuevo PIN"
                  outlined
                  dense
                  type="password"
                  hint="El PIN no es la contraseña de su cuenta. Se usa para autorizar ciertas acciones."
                  persistent-hint
                  :rules="$rule('required|min4|max8')"
                ></v-text-field>
              </template>
            </v-form>
          </div>
          <div class="actions">
            <v-btn
              v-if="newPin !== null"
              @click="newPin = null"
              color="grey"
              dark
              text
            >
              <v-icon left>mdi-close</v-icon>
              Cancelar
            </v-btn>
            <v-btn
              :disabled="!formValid || newPin === null"
              color="primary"
              :loading="saving"
              @click="setPin"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-user-settings {
    @apply tw-flex tw-items-center tw-justify-center tw-h-full tw-p-4;

    .page-inner {
      @apply tw-w-full tw-max-w-2xl;

      .page-card {
        @apply tw-rounded-lg;

        .actions {
          @apply tw-flex tw-justify-center tw-items-center tw-space-x-2 tw-mt-2;
        }
      }
    }
  }
</style>
